import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, ScanSearch, Puzzle, RefreshCw, Radar, GitMerge, TrendingUp } from 'lucide-react';
import { companyInfo, features } from '../data/mock';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    document.title = 'Momentedge | Data Intelligence Infrastructure for Deployed Autonomy';
    document.querySelector('meta[name="description"]')?.setAttribute('content', 'Momentedge helps robotics teams collect high-value data, surface important events, and assemble the context needed to investigate issues and identify root causes.');
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      layers: Layers,
      'scan-search': ScanSearch,
      puzzle: Puzzle,
      'refresh-cw': RefreshCw,
    };
    const IconComponent = icons[iconName] || Layers;
    return <IconComponent size={24} />;
  };

  return (
    <div className="min-h-screen">
      {/* Continuous dark background for entire page */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: `
            linear-gradient(to right, rgb(100, 116, 139) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(100, 116, 139) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}></div>
        {/* Floating accent orbs */}
        <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-12 pb-10 md:pt-16 md:pb-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                {companyInfo.name}
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-300 mb-5 font-medium">
                {companyInfo.tagline}
              </p>

              <div className="prose prose-lg text-slate-400 mb-8">
                <p className="leading-relaxed text-lg">
                  {companyInfo.description}
                </p>
              </div>

              <div>
                <Link
                  to="/resources"
                  data-testid="hero-learn-more-link"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 group"
                >
                  Learn More
                  <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* Right Column - Image */}
            <div className={`relative transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1761857352384-aaae23a714a4?w=800&q=80" 
                  alt="AI and Robotics Technology"
                  className="w-full h-auto object-cover"
                />
                {/* Gradient overlay for better integration */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 to-transparent"></div>
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="relative py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The Challenge
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed" data-testid="challenge-intro">
              Robot fleets generate more data and more edge cases than teams can manually keep up with. Teams can use triggers to capture data around known events, but real-world deployments also produce unexpected behaviors, performance degradation, and incidents no one thought to instrument for. Engineers are left searching and correlating large volumes of multimodal data to understand what happened, while less obvious issues can stay buried — slowing resolution, impacting SLAs, and leaving valuable data out of the development loop.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-lg">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Radar className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">
                The useful signal is sparse
              </h3>
              <p className="text-slate-300 text-center leading-relaxed">
                Hours of normal operation can surround a few seconds of behavior that matter. At fleet scale, storing, transferring, and processing everything with equal priority quickly becomes inefficient.
              </p>
            </div>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-lg">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6">
                <GitMerge className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">
                The evidence lives across modalities
              </h3>
              <p className="text-slate-300 text-center leading-relaxed">
                A single event can span telemetry, system logs, video, perception outputs, and sensor data — each with different formats, frequencies, and timestamps. The useful picture often emerges only when those signals are aligned.
              </p>
            </div>
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50 shadow-lg">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-8 h-8 text-slate-900" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3 text-center">
                What matters changes over time
              </h3>
              <p className="text-slate-300 text-center leading-relaxed">
                The events teams care about evolve as robots encounter new environments, failure modes, and operating conditions. Data infrastructure has to support both known events and questions teams haven't learned to ask yet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative pt-10 pb-16 md:pt-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
              Build a smarter data loop for robotics
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Momentedge is building a data processing and analysis layer that connects what robots collect with what teams learn from their data.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className="bg-slate-800/40 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-slate-700/50 hover:border-slate-600/50"
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center text-slate-900 mb-6">
                  {getIcon(feature.icon)}
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-300 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
